import { Navigate, Outlet, useLocation } from "react-router-dom";
import { isAuthed, getClaims, hasRole, type Role } from "../lib/api.js";
import { defaultRouteForRoles } from "../lib/roles.js";

/**
 * Route guard: render the nested routes only for a live session, else bounce to /auth with a
 * `?next=` so login returns the user to where they were headed. `getClaims() === null` also catches
 * an expired token at navigation time (instead of waiting for an API 401).
 */
export function RequireAuth() {
  const loc = useLocation();
  // Gate on token presence only. An expired token still passes here and is handled by the API's
  // 401 → /auth flow — this avoids ever locking out a signed-in user if claim parsing hiccups.
  if (!isAuthed()) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }
  return <Outlet />;
}

/**
 * Role gate (inside RequireAuth). Client-side roles are for wayfinding only — the API is the
 * authority — so on a role mismatch we just send the user to their own default home rather than
 * showing a hard 403 wall.
 */
export function RequireRole({ allow }: { allow: Role[] }) {
  if (!hasRole(...allow)) {
    return <Navigate to={defaultRouteForRoles(getClaims()?.roles ?? [])} replace />;
  }
  return <Outlet />;
}
